/**
 * NgwEditorImage — `IEditorImage` 의 NgwEditor 본 구현 (PR-N2).
 *
 * 매핑 전략 (05 §4.6, pr-n0-inventory §6.1):
 *   - `attachBlob` / `detachBlob` / `getAttachedBlob`: 어댑터 내부 `Map<id, { blob, uri }>` 로
 *     blob URL 수명을 직접 관리. NgwEditor `upload.url` 비활성 (D-N3) 가정.
 *   - `insert`: `handle.insertHTML('<img src="..." />')` — caller 는 안전한 URI 만 전달.
 *   - `getList(localImageOnly)`: `handle.findNodes('image')` → src/width/height/cid 추출.
 *     localImageOnly=true 면 `src.startsWith('blob:')` 만 반환.
 *   - `convertToBase64(node?)`: node 또는 editor DOM 안의 cid:/blob: src 이미지를 canvas drawImage
 *     로 base64 변환 후 setAttribute. Promise.all 로 모든 변환 완료 보장.
 *   - `openNativePicker`: `handler.openImageChooser?.()` 직접 위임 (D-N6 결정).
 *
 * **누수 방지**: `attachBlob` 으로 발급된 모든 URL 은 `detachBlob` 또는 `disposeBlobs()` 에서
 * 반드시 revoke. `NgwEditorLifecycle.dispose()` 가 root.unmount 직전에 `disposeBlobs()` 호출.
 */

import type {
  AttachedBlobDataUrlHandle,
  AttachedBlobHandle,
  IEditorImage,
  ImageSizePreset,
  SelectedImageState,
} from '../IEditorImage'
import type { EditorImageInfo } from '../types'
import type { NgwEditorAdapter } from './NgwEditorAdapter'
import {
  dpiCorrectedWidth,
  parsePngMetaFromDataUrl,
  resolveReferenceDpi,
  type PngMeta,
} from './pngDpi'

const IMAGE_NODE_NAME = 'image'

/**
 * editor-image-tools — 회전 변형 캐시 상한. 원본 1개당 변형은 최대 17(rotate 4 × flip 4 + 원본)
 * 이므로 64 면 동시에 여러 원본을 다뤄도 충분하며, 초과 시 가장 오래된 항목부터 제거(재생성 가능).
 */
const MAX_VARIANT_CACHE = 64

/** Map 의 삽입 순서(가장 오래된 것 우선)로 size 가 max 를 넘는 동안 앞에서 제거. */
function evictOldest(map: Map<string, unknown>, max: number): void {
  while (map.size > max) {
    const oldest = map.keys().next().value
    if (oldest === undefined) break
    map.delete(oldest)
  }
}

interface BlobEntry {
  blob: Blob
  uri: string
}

/**
 * editor-image-tools — prepareForSend 가 채우는 비파괴 픽셀화 캐시 엔트리.
 * width/height 교환은 편집 시점(rotate)에 이미 attr 로 적용되므로 송신 시 추가 swap 불요.
 */
interface PixelizeEntry {
  /** 픽셀 회전/반전된 결과 dataUrl (schema 안전, 이미 회전된 차원의 비트맵) */
  dataUrl: string
}

export class NgwEditorImage implements IEditorImage {
  private readonly adapter: NgwEditorAdapter
  private readonly blobMap = new Map<string, BlobEntry>()
  /**
   * PR-N9a-1 — data URL (PM 노드 src) → 원본 src (cid:/blob:) 역치환 registry.
   * `IEditorContent.getDataPreprocessed()` 가 본 map 으로 송신 시점에 원본 URL 복원.
   * caller (EmailForm/SignatureEdit `convertCidNgw`, paste 경로 `attachBlobAsDataUrl`) 가 등록.
   */
  private readonly urlMap = new Map<string, string>()
  /**
   * PR-N9a-2 보강 — dataUrl 이미지의 cid 캐싱.
   * NgwEditor 내장 picker/paste/drag 로 직접 주입된 dataUrl 이미지가 매 송신/저장 호출마다
   * 새 cid 로 재생성되는 비효율 (서버 재업로드) 회피를 위한 cache. caller (EmailForm /
   * SignatureEdit onPreProcess 의 data:image/ 분기) 가 신규 cid 생성 후 본 map 에 등록.
   * 다음 호출 시 cache hit 으로 동일 cid + 동일 File 재사용.
   */
  private readonly dataUrlCidMap = new Map<string, { cid: string; file: File }>()
  private idCounter = 0

  /**
   * editor-image-tools — Floating 툴바가 구독하는 현재 선택 이미지 상태.
   * `imageSelection` plugin 이 NodeSelection(image) 진입/이탈 시 `setSelectedImage` 로 갱신.
   */
  private selectedImage: SelectedImageState | null = null
  private readonly selectionSubscribers = new Set<(s: SelectedImageState | null) => void>()

  /**
   * editor-image-tools — 비파괴 픽셀화 캐시 (R2/R3).
   * key = `${currentSrc}|${rotateIndex}|${flip}` (동일 src 복붙+다른 회전 충돌 방지),
   * value = 픽셀 회전된 dataUrl + width/height 교환 여부. `prepareForSend` 가 채우고
   * `NgwEditorContent.getDataPreprocessed` 가 임시 DOM 단계에서 소비.
   */
  private readonly pixelizeCache = new Map<string, PixelizeEntry>()

  /**
   * editor-image-tools (B) — 회전 변형(variant) 캐시. 원본 src + 절대 회전 index(0~3) 기준으로
   * baked dataURL 을 1회만 생성/재사용한다. 동일 (origSrc, rotate) 는 동일 dataURL → 회전을 되돌려도
   * (예: → → ← ←) 동일 결과가 나와 onPreProcess 의 cid 캐시가 재사용되어 **중복 업로드를 방지**한다.
   *   bakedRotationCache: `${origSrc}|${rotate}` → baked dataURL
   *   rotationVariantState: 현재 노드 src → { origSrc, rotate } (다음 회전 계산용)
   */
  private readonly bakedRotationCache = new Map<string, string>()
  private readonly rotationVariantState = new Map<
    string,
    { origSrc: string; rotate: number; flip: number }
  >()
  /**
   * editor-image-tools (dpi) — PNG `data:` URL → 파싱된 픽셀 크기·dpi 캐시. null = 비PNG/파싱 실패.
   * 파싱 비용이 ~2µs 로 무시 가능하나 동일 src 반복 조회(plugin update 매 트랜잭션)를 줄이기 위한 캐시.
   * dpi 는 표시 보정에만 쓰고 노드 attr 로 직렬화하지 않으므로 dispose 시 정리.
   */
  private readonly pngMetaCache = new Map<string, PngMeta | null>()
  /**
   * editor-image-tools (#2) — 회전/반전 bake 직렬화 체인. bakeTransform 은 async(canvas) 라
   * 빠른 연속 클릭이 같은 base src 로 동시 진입하면 두 번째가 stale 가드에 걸려 유실된다. 체인으로
   * 순차 실행해 각 클릭이 직전 결과(갱신된 src/variant)를 읽도록 보장.
   */
  private transformChain: Promise<void> = Promise.resolve()

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
  }

  insert(uri: string): void {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      return
    }
    const escaped = uri.replace(/"/g, '&quot;')
    handle.insertHTML(`<img src="${escaped}" />`)
  }

  getList(localImageOnly?: boolean): EditorImageInfo[] {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      return []
    }
    const nodes = handle.findNodes(IMAGE_NODE_NAME)
    const result: EditorImageInfo[] = []
    for (const entry of nodes) {
      const src = readStringAttr(entry.attrs, 'src')
      if (src == null) {
        continue
      }
      if (localImageOnly === true && !src.startsWith('blob:')) {
        continue
      }
      result.push({
        src,
        cid: readStringAttr(entry.attrs, 'cid'),
        width: readNumericAttr(entry.attrs, 'width') ?? 0,
        height: readNumericAttr(entry.attrs, 'height') ?? 0,
      })
    }
    return result
  }

  async convertToBase64(node?: HTMLElement): Promise<void> {
    const scope = node ?? this.adapter.lifecycle.getHandle()?.getEditorDOM()
    if (scope == null) {
      return
    }
    const imgs = Array.from(scope.querySelectorAll<HTMLImageElement>('img'))
    const targets = imgs.filter((img) => {
      const src = img.getAttribute('src') ?? ''
      return src.startsWith('cid:') || src.startsWith('blob:')
    })
    await Promise.all(targets.map((img) => convertImgToBase64(img)))
  }

  attachBlob(blob: Blob, _opts?: { name?: string; mimeType?: string }): AttachedBlobHandle {
    this.idCounter += 1
    const id = `ngw-blob-${this.idCounter}`
    const uri = URL.createObjectURL(blob)
    this.blobMap.set(id, { blob, uri })
    return { id, uri }
  }

  async attachBlobAsDataUrl(
    blob: Blob,
    _opts?: { name?: string; mimeType?: string },
  ): Promise<AttachedBlobDataUrlHandle> {
    this.idCounter += 1
    const id = `ngw-blob-${this.idCounter}`
    const originalBlobUrl = URL.createObjectURL(blob)
    const dataUrl = await blobToDataUrl(blob)
    // blobMap 은 누수 방지 책임 분리 — originalBlobUrl 을 entry.uri 로 보관해 dispose 시 revoke.
    this.blobMap.set(id, { blob, uri: originalBlobUrl })
    // urlMap 은 getDataPreprocessed 의 역치환 — dataUrl → originalBlobUrl.
    this.urlMap.set(dataUrl, originalBlobUrl)
    return { id, uri: dataUrl, originalBlobUrl }
  }

  detachBlob(uri: string): void {
    for (const [id, entry] of this.blobMap) {
      if (entry.uri === uri) {
        URL.revokeObjectURL(entry.uri)
        this.blobMap.delete(id)
        return
      }
    }
  }

  getAttachedBlob(id: string): { blob: Blob; uri: string } | null {
    const entry = this.blobMap.get(id)
    if (entry == null) {
      return null
    }
    return { blob: entry.blob, uri: entry.uri }
  }

  openNativePicker(): void {
    this.adapter.getHandler()?.openImageChooser?.()
  }

  /**
   * 모든 blob URL 을 revoke 하고 Map 을 비운다.
   * `NgwEditorLifecycle.dispose()` 가 root.unmount 직전에 호출 — 누수 방지.
   * @internal
   */
  disposeBlobs(): void {
    for (const entry of this.blobMap.values()) {
      URL.revokeObjectURL(entry.uri)
    }
    this.blobMap.clear()
    // editor-image-tools (B) — 회전 변형 캐시도 세션 종료 시 정리(dataURL 문자열, revoke 불요).
    this.bakedRotationCache.clear()
    this.rotationVariantState.clear()
    // editor-image-tools (dpi) — PNG 메타 캐시 정리.
    this.pngMetaCache.clear()
    // 선택 구독자 정리(현재 ImageToolbar 는 자체 해제하나 방어적).
    this.selectionSubscribers.clear()
  }

  registerUrlMapping(origSrc: string, dataUrl: string): void {
    this.urlMap.set(dataUrl, origSrc)
  }

  resolveOriginalSrc(dataUrl: string): string | undefined {
    return this.urlMap.get(dataUrl)
  }

  disposeUrlMap(): void {
    this.urlMap.clear()
  }

  registerDataUrlCid(dataUrl: string, cid: string, file: File): void {
    this.dataUrlCidMap.set(dataUrl, { cid, file })
  }

  resolveCidForDataUrl(dataUrl: string): { cid: string; file: File } | undefined {
    return this.dataUrlCidMap.get(dataUrl)
  }

  disposeDataUrlCidMap(): void {
    this.dataUrlCidMap.clear()
  }

  // === editor-image-tools — 선택 상태 추적 (imageSelection plugin → ImageToolbar) ===

  /**
   * `imageSelection` plugin 이 NodeSelection(image) 진입/이탈 시 호출. 구독자(ImageToolbar)에게
   * 통지한다. rotatable 은 송신 픽셀화 가능 여부(인라인 `data:` 이미지 = canvas-safe)로 계산.
   * @internal
   */
  setSelectedImage(state: { pos: number; src: string; dom: HTMLElement } | null): void {
    if (state == null) {
      if (this.selectedImage == null) return
      this.selectedImage = null
    } else {
      // 인라인(data:) 이미지는 canvas 로 직접 픽셀 회전 가능(PM 노드 src 가 자체 완결 비트맵).
      // 외부 http(s) 이미지는 CORS taint 로 toDataURL 이 throw 되어 송신 픽셀화 불가 → 비활성.
      const rotatable = state.src.startsWith('data:')
      // 현재 표시 width 가 어느 프리셋과 일치하는지 계산(불일치 시 null → 메뉴 체크 미표시).
      const size = this.matchSizePreset(state.pos, state.dom)
      this.selectedImage = { ...state, rotatable, size }
    }
    for (const cb of this.selectionSubscribers) cb(this.selectedImage)
  }

  getSelectedImage(): SelectedImageState | null {
    return this.selectedImage
  }

  /** ImageToolbar 가 선택 상태 변화를 구독. 반환된 함수로 해제. @internal */
  subscribeSelection(cb: (state: SelectedImageState | null) => void): () => void {
    this.selectionSubscribers.add(cb)
    return () => {
      this.selectionSubscribers.delete(cb)
    }
  }

  // === editor-image-tools — 회전/반전/크기 (편집 = 내장 커맨드/CSS) ===

  rotate(direction: 'left' | 'right'): void {
    // 회전·반전 모두 원본 기준 절대 변형(rotate index 0~3 + flip bits)으로 즉시 픽셀 bake 한다.
    // CSS(scaleX/Y) 반전 + 픽셀 회전을 섞으면 합성 순서(rotate-then-screenflip)가 어긋나 홀수 flip
    // 상태에서 회전 시 축이 반대로 나온다. canvas 한 곳에서 flip→rotate 순서로 합성해 정합 보장.
    this.enqueueTransform({ rotateStep: direction === 'right' ? 1 : 3 })
  }

  flip(axis: 'horizontal' | 'vertical'): void {
    this.enqueueTransform({ flipAxis: axis })
  }

  /** (#2) bake 를 직렬 체인에 큐잉 — 빠른 연속 클릭이 직전 결과를 읽도록 순차 실행. */
  private enqueueTransform(op: {
    rotateStep?: number
    flipAxis?: 'horizontal' | 'vertical'
  }): void {
    this.transformChain = this.transformChain
      .catch(() => undefined)
      .then(() => this.bakeTransform(op))
  }

  /**
   * 회전/반전 공통 본체 — 현재 변형(원본 src + 절대 rotate index + flip bits)에 한 단계 적용 후
   * `rotateFlipDataUrl(origSrc, rotate, flip)` 로 baked 변형을 생성/재사용(캐시)하여 src 교체.
   * 모든 변형이 픽셀에 반영되므로 dataNineRotate/FlipH/FlipV 는 0 으로 두어 내장 CSS 이중 적용 방지.
   */
  private async bakeTransform(op: {
    rotateStep?: number
    flipAxis?: 'horizontal' | 'vertical'
  }): Promise<void> {
    const core = this.adapter.lifecycle.getCore()
    const handle = this.adapter.lifecycle.getHandle()
    // 가드: 툴바가 떠 있는(=NodeSelection(image)) 경우에만(R2-G4).
    if (core == null || handle == null || this.selectedImage == null) return
    const { pos, dom } = this.selectedImage
    const entry = handle.findNodes(IMAGE_NODE_NAME).find((e) => e.pos === pos)
    if (entry == null) return
    const src = readStringAttr(entry.attrs, 'src')
    if (src == null || !src.startsWith('data:')) return // canvas-safe(인라인) 만

    // 현재 변형 해석. 미등록(최초/레거시/재로드)이면 현재 src 를 원본으로 보고 레거시 dataNine* 흡수.
    const base = this.resolveTransformVariant(src, entry.attrs)
    const isRotate = op.rotateStep != null
    const nextRotate = isRotate ? (base.rotate + op.rotateStep!) % 4 : base.rotate
    // 반전: 사용자가 클릭한 화면 축 → 원본 프레임 축. 90°/270°(홀수 회전)에서는 H↔V 가 교환된다.
    let nextFlip = base.flip
    if (op.flipAxis != null) {
      const odd = base.rotate % 2 === 1
      const bit = op.flipAxis === 'horizontal' ? (odd ? 1 : 2) : odd ? 2 : 1
      nextFlip = base.flip ^ bit
    }

    // 확대/축소가 반영된 현재 표시 크기 측정(회전 90° 단위 → 박스 width 교환). CSS 변형 미사용이라 rect 정확.
    const imgEl = dom.tagName === 'IMG' ? dom : dom.querySelector('img')
    const rect = imgEl?.getBoundingClientRect()
    const dispWidth = rect != null ? Math.round(rect.width) : 0
    const dispHeight = rect != null ? Math.round(rect.height) : 0

    let baked: string
    try {
      baked = await this.bakeVariant(base.origSrc, nextRotate, nextFlip)
    } catch {
      return // 이미지 로드/캔버스 실패 시 무시(원본 유지).
    }

    // async 경계 — 대기 중 dispose/remount 됐을 수 있으므로 core/handle 을 어댑터에서 **재조회**한다.
    // (await 전 캡처한 core/handle 은 파괴된 view 를 가리킬 수 있어, 그대로 dispatch 하면 파괴된
    // ProseMirror view 에 트랜잭션을 던지게 됨 — Critical race.) dispose 시 getCore()=null → 안전 종료.
    const freshCore = this.adapter.lifecycle.getCore()
    const freshHandle = this.adapter.lifecycle.getHandle()
    if (freshCore == null || freshHandle == null) return
    // 선택/문서가 바뀌었을 수 있으므로 pos+src 로 재확인(stale 가드).
    const fresh = freshHandle
      .findNodes(IMAGE_NODE_NAME)
      .find((e) => e.pos === pos && readStringAttr(e.attrs, 'src') === src)
    if (fresh == null) return

    // 모든 변형이 픽셀에 반영됨 → dataNine* 는 null 로 비워 toDOM 직렬화 자체를 막는다(0/'false'
    // 도 != null 이라 직렬화되어 송신/draft HTML 에 내부 메타가 남는 유출 방지, #3). 변형 추적은
    // rotationVariantState 가 담당하므로 attr 의존 불요. 누적 style 치수 제거.
    const attrs: Record<string, unknown> = {
      ...fresh.attrs,
      src: baked,
      dataNineRotate: null,
      dataNineFlipH: null,
      dataNineFlipV: null,
      style: stripDimensionStyle(fresh.attrs.style),
    }
    // width·height 를 표시 비율대로 명시 계산한다. height 를 null 로 두면 직렬화에서 빠지고, 재편집
    // 시 라이브러리 parseDOM 이 element.height(=naturalHeight)를 주워 비율이 깨진다(세로 늘어남).
    // 회전 90° 는 표시 폭↔높이가 교환되고, 반전은 차원 불변. CSS 변형 미사용이라 측정한 disp* 가
    // 정확하므로 이를 기준으로 비율을 보존한다(setSize 와 동일 처방).
    const baseWidth = isRotate ? dispHeight : dispWidth
    const baseHeight = isRotate ? dispWidth : dispHeight
    if (baseWidth > 0 && baseHeight > 0) {
      let newWidth = baseWidth
      // src reload 시 내장 handleImageLoad 가 width>editorMaxWidth 면 비율 재계산하므로 미리 clamp.
      const editorWidth = this.resolveEditorContentWidth()
      if (editorWidth > 0 && newWidth > editorWidth) newWidth = editorWidth
      attrs.width = newWidth
      attrs.height = Math.round((newWidth * baseHeight) / baseWidth)
    } else {
      // 표시 크기 미측정(rect 없음) — 비율 단서가 없으므로 height 는 라이브러리 자동에 맡긴다.
      attrs.height = null
    }
    freshCore.view.dispatch(freshCore.view.state.tr.setNodeMarkup(pos, undefined, attrs))
    // 변형 상태 등록(다음 회전/반전 계산용).
    this.rotationVariantState.set(baked, {
      origSrc: base.origSrc,
      rotate: nextRotate,
      flip: nextFlip,
    })
  }

  /**
   * 현재 노드 src 의 변형 기준점 해석. variantState 에 있으면 그 값을, 없으면(최초/레거시/재로드)
   * 현재 src 를 원본으로 보고 레거시 dataNineRotate(degrees)/FlipH/FlipV 를 흡수.
   */
  private resolveTransformVariant(
    src: string,
    attrs: Record<string, unknown>,
  ): { origSrc: string; rotate: number; flip: number } {
    const known = this.rotationVariantState.get(src)
    if (known != null) return known
    const rotate = normalizeRotateIndex(readNumericAttr(attrs, 'dataNineRotate') ?? 0)
    const flip =
      (readBooleanAttr(attrs, 'dataNineFlipH') ? 2 : 0) +
      (readBooleanAttr(attrs, 'dataNineFlipV') ? 1 : 0)
    return { origSrc: src, rotate, flip }
  }

  /** 원본 src 를 절대 (rotate index 0~3, flip bits)로 baked dataURL 생성(캐시). 변형 없음은 원본. */
  private async bakeVariant(origSrc: string, rotate: number, flip: number): Promise<string> {
    if (rotate === 0 && flip === 0) return origSrc
    const key = `${origSrc}|${rotate}|${flip}`
    const cached = this.bakedRotationCache.get(key)
    if (cached != null) return cached
    const baked = await rotateFlipDataUrl(origSrc, rotate, flip)
    this.bakedRotationCache.set(key, baked)
    // 원본도 variantState 에 등록(되돌림 시 rotate=0,flip=0 → origSrc 인식).
    if (!this.rotationVariantState.has(origSrc)) {
      this.rotationVariantState.set(origSrc, { origSrc, rotate: 0, flip: 0 })
    }
    // 장기 세션에서 서로 다른 원본을 다수 회전 후 삭제·재삽입하면 캐시가 무한 증가하므로 상한을
    // 둔다. Map 의 삽입 순서를 이용해 가장 오래된 항목부터 제거(evict 시 다음 회전에서 재생성될 뿐
    // 데이터 손실 없음).
    evictOldest(this.bakedRotationCache, MAX_VARIANT_CACHE)
    evictOldest(this.rotationVariantState, MAX_VARIANT_CACHE)
    return baked
  }

  setSize(size: ImageSizePreset): void {
    const core = this.adapter.lifecycle.getCore()
    const handle = this.adapter.lifecycle.getHandle()
    if (core == null || handle == null || this.selectedImage == null) return
    const { pos, dom } = this.selectedImage
    const entry = handle.findNodes(IMAGE_NODE_NAME).find((e) => e.pos === pos)
    if (entry == null) return
    const presetWidth = this.presetWidth(size, dom, readStringAttr(entry.attrs, 'src'))
    if (presetWidth == null) return
    // width 와 함께 height 도 natural 비율에 맞춰 명시 계산한다(드래그 리사이즈와 동일). height 를
    // null 로 두면 라이브러리가 wrapper 높이로 naturalHeight 를 그대로 써 비율이 어긋난다. 또 노드
    // style attr 에 직전 저장/드래그에서 누적된 width/height 선언이 남아 있으면 toDOM 이 잘못된 값을
    // 직렬화하므로 style 의 치수 선언은 제거한다.
    const natural = resolveNaturalSize(dom)
    const height =
      natural.width > 0 ? Math.round((presetWidth * natural.height) / natural.width) : null
    const tr = core.view.state.tr.setNodeMarkup(pos, undefined, {
      ...entry.attrs,
      width: presetWidth,
      height,
      style: stripDimensionStyle(entry.attrs.style),
      dataNineSize: size,
    })
    core.view.dispatch(tr)
  }

  /**
   * 프리셋별 목표 width(px) 계산. 기존 tinymce 동작과 동일(nine_tinymce_desktop 의 getMaxAreaSize
   * 기준)하게 25/50/100% 는 **편집영역 폭** 기준으로 적용한다(이미지 원본 크기 무관).
   *   자동(0): min(naturalWidth × 72/dpi, editorWidth) — 고DPI PNG 는 dpi 비율로 축소(tinymce
   *            setImgMaxSize 동일). 이미지가 에디터보다 크면 에디터 폭, 작으면 보정된 원본.
   *   25/50/100%(1/2/3): editorWidth × ratio — 100% 는 편집영역을 꽉 채운다(dpi 무관, tinymce 동일).
   * naturalWidth(자동 비율 단서) 또는 editorWidth 미측정 시 null.
   */
  private presetWidth(size: ImageSizePreset, dom: HTMLElement, src?: string): number | null {
    const naturalWidth = resolveNaturalWidth(dom)
    if (naturalWidth <= 0) return null
    const editorWidth = this.resolveEditorContentWidth()
    if (size === 0) {
      const corrected =
        src != null ? this.dpiCorrectedNaturalWidth(naturalWidth, src) : naturalWidth
      return editorWidth > 0 ? Math.min(corrected, editorWidth) : corrected
    }
    if (editorWidth <= 0) return null
    const ratio = size === 1 ? 0.25 : size === 2 ? 0.5 : 1
    return Math.round(editorWidth * ratio)
  }

  // === editor-image-tools (dpi) — PNG 해상도 보정 (tinymce setImgMaxSize 재현) ===

  /** PNG `data:` URL 의 픽셀 크기·dpi 파싱 결과(캐시). 비PNG/실패 시 null. @internal */
  getPngMeta(src: string): PngMeta | null {
    const cached = this.pngMetaCache.get(src)
    if (cached !== undefined) return cached
    const meta = parsePngMetaFromDataUrl(src)
    this.pngMetaCache.set(src, meta)
    return meta
  }

  /** naturalWidth 에 src 의 dpi 보정(referenceDpi/dpi)을 적용. 비PNG/dpi 미기록/기준일치 면 그대로. */
  private dpiCorrectedNaturalWidth(naturalWidth: number, src: string): number {
    const meta = this.getPngMeta(src)
    return meta != null
      ? dpiCorrectedWidth(naturalWidth, meta.dpi, resolveReferenceDpi())
      : naturalWidth
  }

  /**
   * imageDpiAutoSize plugin 전용 — **삽입 직후(width 미설정)** PNG image 에 적용할 dpi 보정 크기.
   * 라이브러리 handleImageLoad 가 width 를 박기 전에 plugin 이 먼저 보정값을 주입하면(≤ editorWidth)
   * 라이브러리가 덮어쓰지 않는다. 보정 대상이 아니면 null:
   *   - width 가 이미 설정됨(라이브러리 자동/사용자 드래그·프리셋) → 미개입
   *   - 명시 프리셋(dataNineSize 1/2/3) → 미개입
   *   - 비PNG 또는 dpi 미기록/기준(referenceDpi)일치(보정 불필요, 라이브러리 기본값과 동일) → 미개입
   * @internal
   */
  autoDpiSizeForInsert(
    src: string,
    currentWidth: unknown,
    dataNineSize: unknown,
  ): { width: number; height: number } | null {
    if (currentWidth != null) return null
    if (dataNineSize === 1 || dataNineSize === 2 || dataNineSize === 3) return null
    const meta = this.getPngMeta(src)
    if (meta == null) return null
    const referenceDpi = resolveReferenceDpi()
    if (meta.dpi == null || meta.dpi === referenceDpi) return null
    const corrected = dpiCorrectedWidth(meta.width, meta.dpi, referenceDpi)
    const editorWidth = this.resolveEditorContentWidth()
    const width = editorWidth > 0 ? Math.min(corrected, editorWidth) : corrected
    const height = Math.round((width * meta.height) / meta.width)
    return { width, height }
  }

  /**
   * 현재 노드 width 가 어느 프리셋(자동/25/50/100)과 일치하는지 판정. 일치하지 않으면(예: resize
   * handle 로 임의 조정) null → 메뉴 체크 미표시.
   *
   * 자동과 100% 는 이미지가 에디터보다 작을 때 폭이 동일하므로 폭만으로는 구분이 불가하다. 따라서
   * 사용자가 명시 선택해 저장해 둔 `dataNineSize` 를 우선 확인하고, 현재 폭이 그 프리셋과 일치하면
   * 저장값을 그대로 반환한다(100% 선택 후 자동으로 표시되던 문제 수정). 일치하지 않으면(drag 조정 등)
   * 폭 기반 탐색으로 폴백.
   */
  private matchSizePreset(pos: number, dom: HTMLElement): ImageSizePreset | null {
    const handle = this.adapter.lifecycle.getHandle()
    const entry = handle?.findNodes(IMAGE_NODE_NAME).find((e) => e.pos === pos)
    if (entry == null) return null
    const imgEl = dom.tagName === 'IMG' ? (dom as HTMLImageElement) : dom.querySelector('img')
    const currentWidth =
      readNumericAttr(entry.attrs, 'width') ?? Math.round(imgEl?.getBoundingClientRect().width ?? 0)
    if (currentWidth <= 0) return null
    const tolerance = 1.5
    const editorWidth = this.resolveEditorContentWidth()

    // 프리셋 목표 폭. 25/50/100% 는 editorWidth 기준이라 항상 editorWidth 이하이고 자동도
    // min(natural, editorWidth) 라, 라이브러리 handleImageLoad 의 editorWidth clamp 가 발동하지
    // 않는다. Math.min 은 방어적 보정(외부에서 width 가 editorWidth 를 넘게 주입된 경우)으로 남긴다.
    const src = readStringAttr(entry.attrs, 'src')
    const expectedDisplayWidth = (size: ImageSizePreset): number | null => {
      const target = this.presetWidth(size, dom, src)
      if (target == null) return null
      return editorWidth > 0 ? Math.min(target, editorWidth) : target
    }

    // 저장된 프리셋 우선 — 현재 폭이 그 프리셋(clamp 반영)과 일치하면 그대로 반환(자동 vs 100% 모호성 해소).
    const stored = readNumericAttr(entry.attrs, 'dataNineSize')
    if (stored === 0 || stored === 1 || stored === 2 || stored === 3) {
      const expected = expectedDisplayWidth(stored)
      if (expected != null && Math.abs(currentWidth - expected) <= tolerance) return stored
    }

    const presets: ImageSizePreset[] = [0, 1, 2, 3]
    for (const preset of presets) {
      const expected = expectedDisplayWidth(preset)
      if (expected != null && Math.abs(currentWidth - expected) <= tolerance) return preset
    }
    return null
  }

  /**
   * 에디터 콘텐츠 영역 폭 — 내장 ImageNodeView 의 editorMaxWidth(rect.width − padding − 20) 와
   * 동일하게 계산하여 '자동' 폭이 라이브러리 클램프와 일치하도록 한다.
   */
  private resolveEditorContentWidth(): number {
    const dom = this.adapter.lifecycle.getHandle()?.getEditorDOM()
    if (dom == null) return 0
    const rect = dom.getBoundingClientRect()
    const style = getComputedStyle(dom)
    const paddingLeft = Number.parseFloat(style.paddingLeft) || 0
    const paddingRight = Number.parseFloat(style.paddingRight) || 0
    return Math.max(0, rect.width - paddingLeft - paddingRight - 20)
  }

  // === editor-image-tools — 전송 픽셀화 (비파괴 캐시, design §8) ===

  /**
   * 송신 직전 1회. PM 을 변경하지 않고, CSS transform 으로만 표현된 회전/반전 이미지를 실제
   * 픽셀로 굽는다. PM 노드의 `data:` URL(자체 완결 비트맵)을 canvas 로 직접 회전/반전하여 새
   * dataUrl 을 `pixelizeCache` 에 채운다. `getDataPreprocessed` 가 임시 DOM 에서 소비.
   * 멱등 — 매번 clear 후 재계산. parent `processImageMap`/`getProcessedImage` 에 의존하지 않으므로
   * 답장 인용·붙여넣기·임시저장 복원 이미지 모두 처리 가능.
   */
  async prepareForSend(): Promise<void> {
    this.pixelizeCache.clear()
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) return

    for (const entry of handle.findNodes(IMAGE_NODE_NAME)) {
      const src = readStringAttr(entry.attrs, 'src')
      if (src == null || !src.startsWith('data:')) continue // canvas-safe(인라인) 만
      const rotate = normalizeRotateIndex(readNumericAttr(entry.attrs, 'dataNineRotate') ?? 0)
      const flip =
        (readBooleanAttr(entry.attrs, 'dataNineFlipH') ? 2 : 0) +
        (readBooleanAttr(entry.attrs, 'dataNineFlipV') ? 1 : 0)
      if (rotate === 0 && flip === 0) continue

      try {
        const dataUrl = await rotateFlipDataUrl(src, rotate, flip)
        this.pixelizeCache.set(pixelizeKey(src, rotate, flip), { dataUrl })
      } catch {
        // 픽셀화 실패(이미지 로드 실패 등) 시 해당 이미지는 회전 미반영(원본 유지). 안전망.
      }
    }
  }

  /**
   * `getDataPreprocessed` 가 임시 DOM `<img>` 의 (src, degrees, flipH, flipV) 으로 픽셀화 결과 조회.
   * prepareForSend 와 동일 키 정규화(degrees→index) 를 사용해 미스를 방지.
   * @internal
   */
  consumePixelized(
    src: string,
    deg: number,
    flipH: boolean,
    flipV: boolean,
  ): PixelizeEntry | undefined {
    const rotate = normalizeRotateIndex(deg)
    const flip = (flipH ? 2 : 0) + (flipV ? 1 : 0)
    return this.pixelizeCache.get(pixelizeKey(src, rotate, flip))
  }

  /** 픽셀화 캐시가 비어있지 않으면 송신 컨텍스트(strip 수행). @internal */
  hasPixelizations(): boolean {
    return this.pixelizeCache.size > 0
  }

  /** 송신 흐름 종료/문서 편집 시 캐시 무효화. @internal */
  clearPixelizeCache(): void {
    this.pixelizeCache.clear()
  }

  /**
   * setData 후 PM doc 의 image attrs.width/height 가 ImageNodeView 의 layout race (popup
   * 좁은 mount 시점의 작은 editorMaxWidth 측정) 로 변형된 경우, `srcMap` 의 원본 값으로
   * 복원. attrs.src 를 키로 매칭. 이미 원본과 일치하면 no-op.
   */
  restoreImageDimensions(srcMap: Map<string, { width: number; height: number }>): void {
    if (srcMap.size === 0) return
    const handle = this.adapter.lifecycle.getHandle()
    const core = this.adapter.lifecycle.getCore()
    if (handle == null || core == null) return
    const { view } = core
    const nodes = handle.findNodes(IMAGE_NODE_NAME)
    if (nodes.length === 0) return

    let tr = view.state.tr
    let modified = false
    for (const entry of nodes) {
      const src = readStringAttr(entry.attrs, 'src')
      if (src == null) continue
      const orig = srcMap.get(src)
      if (orig == null) continue
      const currentWidth = readNumericAttr(entry.attrs, 'width')
      const currentHeight = readNumericAttr(entry.attrs, 'height')
      if (currentWidth === orig.width && currentHeight === orig.height) continue
      tr = tr.setNodeMarkup(entry.pos, undefined, {
        ...entry.attrs,
        width: orig.width,
        height: orig.height,
      })
      modified = true
    }
    if (modified) view.dispatch(tr)
  }
}

function readStringAttr(attrs: Record<string, unknown>, key: string): string | undefined {
  const value = attrs[key]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function readNumericAttr(attrs: Record<string, unknown>, key: string): number | undefined {
  const value = attrs[key]
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return undefined
}

/** editor-image-tools — dataNineFlipH/V 는 내장 schema 상 string "true"/"false" (또는 boolean). */
function readBooleanAttr(attrs: Record<string, unknown>, key: string): boolean {
  const value = attrs[key]
  return value === true || value === 'true'
}

/** editor-image-tools — 선택 노드 DOM(또는 그 안의 img)에서 이미지 naturalWidth 추출. 미로드 시 0. */
function resolveNaturalWidth(dom: HTMLElement): number {
  return resolveNaturalSize(dom).width
}

/** editor-image-tools — 이미지 natural 가로/세로 추출. 미로드 시 {0,0}. */
function resolveNaturalSize(dom: HTMLElement): { width: number; height: number } {
  const img = dom.tagName === 'IMG' ? (dom as HTMLImageElement) : dom.querySelector('img')
  return { width: img?.naturalWidth ?? 0, height: img?.naturalHeight ?? 0 }
}

/**
 * editor-image-tools — style 문자열에서 width/height 선언만 제거(나머지 보존).
 *
 * 내장 image toDOM 은 노드 `style` attr 에 들어있는 `width:..px;height:..px` 를 그대로 직렬화한다.
 * 프리셋/회전은 size 를 width attr 로 제어하고 height 는 비율 자동(미지정)이어야 하므로, 직전
 * 저장/드래그에서 누적된 style 의 치수 선언을 제거해 viewer 가 자연 비율로 렌더하도록 한다.
 * (드래그 리사이즈만 명시적 width/height 를 유지.)
 */
function stripDimensionStyle(style: unknown): string | null {
  if (typeof style !== 'string' || style.length === 0) return null
  const cleaned = style
    .split(';')
    .map((decl) => decl.trim())
    .filter((decl) => decl.length > 0 && !/^(width|height)\s*:/i.test(decl))
    .join('; ')
  return cleaned.length > 0 ? cleaned : null
}

/**
 * editor-image-tools — 내장 degrees(0/90/180/270) → getProcessedImage index(0~3) 변환.
 * 45° 등 비정상값은 0 으로 clamp(design §8.2, R1-F7).
 */
function normalizeRotateIndex(deg: number): number {
  const norm = (((Math.round(deg) % 360) + 360) % 360) / 90
  return Number.isInteger(norm) && norm >= 0 && norm <= 3 ? norm : 0
}

/**
 * editor-image-tools — 픽셀화 캐시 복합 키 (R3-G2). 동일 src 가 회전 다르게 복붙된 경우 충돌 방지.
 * prepareForSend(채움)·consumePixelized(소비) 양쪽이 동일 키를 쓴다.
 */
function pixelizeKey(src: string, rotate: number, flip: number): string {
  return `${src}|${rotate}|${flip}`
}

/**
 * editor-image-tools — `data:` URL 이미지를 canvas 로 회전(0~3 ×90°)·반전(flip bit: 2=H,1=V)하여
 * 새 PNG dataUrl 을 반환. EmailForm.getProcessedImage 의 검증된 회전 수식을 그대로 사용(동일
 * dataNine 시맨틱) — 단 parent processImageMap 의존 없이 src(dataUrl) 에서 직접 비트맵을 로드한다.
 */
function rotateFlipDataUrl(src: string, rotate: number, flip: number): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const img = new Image()
    img.onload = (): void => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (ctx == null) {
          reject(new Error('rotateFlipDataUrl: no 2d context'))
          return
        }
        const nw = img.naturalWidth
        const nh = img.naturalHeight
        let width = Math.max(nw, nh)
        let height = width
        let top = 0
        let left = 0
        if (nw > nh) {
          top = (nw - nh) / 2
        } else {
          left = (nh - nw) / 2
        }
        canvas.width = width
        canvas.height = height

        const degrees = rotate * 90
        const flipH = (flip >> 1) & 1
        const flipV = (flip >> 0) & 1
        const scaleH = flipH ? -1 : 1
        const scaleV = flipV ? -1 : 1
        const posX = flipH ? width * -1 : 0
        const posY = flipV ? height * -1 : 0

        ctx.save()
        if (rotate === 1 || rotate === 3) {
          ctx.translate(-top, -left)
          width = nh
          height = nw
        } else {
          ctx.translate(-left, -top)
          width = nw
          height = nh
        }
        ctx.translate(canvas.width / 2, canvas.height / 2)
        ctx.rotate((degrees * Math.PI) / 180)
        ctx.translate(-canvas.width / 2, -canvas.height / 2)
        ctx.scale(scaleH, scaleV)
        ctx.drawImage(img, posX + left, posY + top, nw, nh)
        ctx.restore()

        const imgData = ctx.getImageData(0, 0, width, height)
        canvas.width = width
        canvas.height = height
        ctx.putImageData(imgData, 0, 0)

        // 입력 MIME 보존 — JPEG 사진을 PNG 로 강제 변환하면 base64 크기가 3~5배 팽창한다.
        // 변형은 항상 원본 기준 1회 인코딩(캐시)이라 JPEG 세대 손실 누적 없음. JPEG 외에는(투명도
        // 가능성) 안전하게 PNG 유지.
        const isJpeg = src.startsWith('data:image/jpeg') || src.startsWith('data:image/jpg')
        const mime = isJpeg ? 'image/jpeg' : 'image/png'
        resolve(mime === 'image/jpeg' ? canvas.toDataURL(mime, 0.92) : canvas.toDataURL(mime))
      } catch (err) {
        reject(err instanceof Error ? err : new Error('rotateFlipDataUrl: canvas export failed'))
      }
    }
    img.onerror = (): void => reject(new Error('rotateFlipDataUrl: image load failed'))
    img.src = src
  })
}

/**
 * PR-N9a-1 — Blob 을 data URL 문자열로 변환. `FileReader.readAsDataURL` 비동기 결과를 await.
 *
 * `attachBlobAsDataUrl` 와 EmailForm/SignatureEdit `convertCidNgw` 양쪽에서 사용한다.
 * jsdom 환경에서도 FileReader 가 동작하므로 단위 테스트 mock 없이 통과 — 단, 매우 큰 Blob
 * 처리 시간은 환경에 의존.
 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (): void => {
      const result = reader.result
      if (typeof result === 'string') {
        resolve(result)
      } else {
        reject(new Error('FileReader.readAsDataURL did not return a string'))
      }
    }
    reader.onerror = (): void => {
      reject(reader.error ?? new Error('FileReader.readAsDataURL failed'))
    }
    reader.readAsDataURL(blob)
  })
}

function convertImgToBase64(img: HTMLImageElement): Promise<void> {
  return new Promise<void>((resolve) => {
    const finalize = (dataUrl: string | null): void => {
      if (dataUrl != null) {
        img.setAttribute('src', dataUrl)
      }
      resolve()
    }

    const drawAndExport = (source: HTMLImageElement): void => {
      try {
        const canvas = source.ownerDocument.createElement('canvas')
        canvas.width = source.naturalWidth || source.width
        canvas.height = source.naturalHeight || source.height
        const ctx = canvas.getContext('2d')
        if (ctx == null) {
          finalize(null)
          return
        }
        ctx.drawImage(source, 0, 0)
        finalize(canvas.toDataURL('image/png'))
      } catch {
        // toDataURL 은 cross-origin 등으로 throw 가능 — silent fallback.
        finalize(null)
      }
    }

    if (img.complete && img.naturalWidth > 0) {
      drawAndExport(img)
      return
    }

    const loader = new Image()
    loader.crossOrigin = 'anonymous'
    loader.onload = (): void => drawAndExport(loader)
    loader.onerror = (): void => finalize(null)
    loader.src = img.getAttribute('src') ?? ''
  })
}
